import React, { useState, useEffect, useRef } from 'react';
import { useAppContext } from '../context/AppContext';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Form,
  Button,
  Alert,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';
import { FaSave, FaTimes, FaReceipt, FaCheck } from 'react-icons/fa';

const getAuthToken = () => localStorage.getItem('authToken');

const calculateInstallmentTotal = (installments) =>
  installments.reduce(
    (sum, installment) =>
      sum +
      (installment.is_selected ? Number(installment.allocated_amount) || 0 : 0),
    0,
  );

const canInteractWithInstallment = (installment, academicYearMismatch) =>
  !academicYearMismatch &&
  (installment.is_selectable_for_receipt || installment.is_editable_existing);

const getInstallmentEditableBalance = (installment) => {
  const editableBalance = Number(
    installment.editable_balance_amount ?? installment.balance_amount ?? 0,
  );
  const allocatedAmount = Number(installment.allocated_amount ?? 0);

  return Math.max(editableBalance, allocatedAmount, 0);
};

const EditReceipt = () => {
  const { selectedAcademicYear } = useAppContext();
  // Dropdown keyboard navigation state
  const [fromDropdownIndex, setFromDropdownIndex] = useState(-1);
  const [toDropdownIndex, setToDropdownIndex] = useState(-1);
  const [loadingInstallments, setLoadingInstallments] = useState(false);
  const [feeSummary, setFeeSummary] = useState(null);
  // Description expand/collapse state and ref
  const [descExpanded, setDescExpanded] = useState(false);
  const descRef = useRef(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [receipt, setReceipt] = useState({
    date: '',
    receipt_number: '',
    from_account: '',
    to_account: '',
    from_account_id: '',
    to_account_id: '',
    amount: '',
    description: '',
    admission_no: '',
    academic_year_id: '',
    fee_installments: [],
  });
  const [feeInstallments, setFeeInstallments] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [helperAcademicYear, setHelperAcademicYear] = useState(null);
  const [academicYearMismatch, setAcademicYearMismatch] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const alertTimeoutRef = useRef(null);
  const [showFromDropdown, setShowFromDropdown] = useState(false);
  const [showToDropdown, setShowToDropdown] = useState(false);
  const [filteredFromAccounts, setFilteredFromAccounts] = useState([]);
  const [filteredToAccounts, setFilteredToAccounts] = useState([]);
  const fromDropdownRef = useRef(null);
  const toDropdownRef = useRef(null);
  // Ref for date input (F2 shortcut)
  const dateInputRef = useRef(null);

  // Dynamic textarea height for description
  useEffect(() => {
    if (descRef.current) {
      descRef.current.style.height = 'auto';
      descRef.current.style.height = descRef.current.scrollHeight + 'px';
    }
  }, [receipt.description, descExpanded]);

  // Keyboard shortcuts: Ctrl+S (save), Esc (cancel), Enter (submit if valid)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (!loading) handleSubmit(e);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        if (!loading) navigate(-1);
      } else if (e.key === 'F2') {
        e.preventDefault();
        if (dateInputRef.current) dateInputRef.current.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [loading, receipt, navigate]);

  useEffect(() => {
    fetchReceipt();
    fetchAccounts();
    fetchCashAccounts();
  }, []);

  // Set academic_year_id from context
  useEffect(() => {
    if (selectedAcademicYear && selectedAcademicYear.id) {
      setReceipt((prev) => ({
        ...prev,
        academic_year_id: selectedAcademicYear.id,
      }));

      // Reload the current receipt when academic year changes to enforce API mismatch handling.
      fetchReceipt();
    }
  }, [selectedAcademicYear]);

  // No auto-fetch on admission/date for edit receipt; we use explicit call on blur/enter.
  useEffect(() => {
    // intentionally empty
  }, [receipt.admission_no, receipt.academic_year_id]);

  // Floating alert auto-dismiss
  useEffect(() => {
    if (alert.show) {
      if (alertTimeoutRef.current) clearTimeout(alertTimeoutRef.current);
      alertTimeoutRef.current = setTimeout(() => {
        setAlert({ show: false, message: '', variant: '' });
      }, 2000);
    }
    return () => {
      if (alertTimeoutRef.current) clearTimeout(alertTimeoutRef.current);
    };
  }, [alert.show]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        fromDropdownRef.current &&
        !fromDropdownRef.current.contains(event.target)
      ) {
        setShowFromDropdown(false);
      }
      if (
        toDropdownRef.current &&
        !toDropdownRef.current.contains(event.target)
      ) {
        setShowToDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Helper to fetch student installments for editing
  const fetchStudentFeeInstallments = async (
    admissionNo,
    academicYearId,
    existingInstallments = [],
  ) => {
    const token = getAuthToken();
    if (!token || !admissionNo || !academicYearId) return;
    setLoadingInstallments(true);
    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get_student_fee_receipt_helper`,
        {
          student_admission_no: admissionNo,
          academic_year_id: academicYearId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      if (response.data.status === 'success') {
        const helperYear = response.data.academic_year || null;
        const mismatch =
          helperYear &&
          selectedAcademicYear?.id &&
          helperYear.id !== selectedAcademicYear.id;

        setHelperAcademicYear(helperYear);
        setAcademicYearMismatch(mismatch);
        setSelectedStudent(response.data.student);
        setFeeSummary(response.data.summary);

        // Merge existing allocations from receipt
        const existing = existingInstallments.reduce((acc, inst) => {
          acc[inst.student_fee_installment_id] = inst;
          return acc;
        }, {});

        const helperInstallments = response.data.installments || [];
        const helperInstallmentIds = new Set(
          helperInstallments.map((inst) => inst.student_fee_installment_id),
        );

        const mergedInstallments = helperInstallments.map((inst) => {
          const existingInstallment = existing[inst.student_fee_installment_id];
          const allocatedAmount = existingInstallment?.allocated_amount || '';
          const editableBalanceAmount =
            Number(inst.balance_amount) + Number(allocatedAmount || 0);

          return {
            ...existingInstallment,
            ...inst,
            allocated_amount: allocatedAmount,
            editable_balance_amount: editableBalanceAmount,
            is_selected: Number(allocatedAmount) > 0,
            is_editable_existing: !!existingInstallment,
          };
        });

        existingInstallments.forEach((inst) => {
          if (!helperInstallmentIds.has(inst.student_fee_installment_id)) {
            mergedInstallments.push({
              ...inst,
              editable_balance_amount: getInstallmentEditableBalance(inst),
              is_selected: Number(inst.allocated_amount) > 0,
              is_editable_existing: true,
            });
          }
        });

        setFeeInstallments(mergedInstallments);
        setReceipt((prev) => ({
          ...prev,
          amount: calculateInstallmentTotal(mergedInstallments).toString(),
        }));

        if (mismatch) {
          setAlert({
            show: true,
            message: `Academic year mismatch: installments are for ${helperYear?.name} but selected year is ${selectedAcademicYear?.name || 'N/A'}. Receipts cannot be saved until year matches.`,
            variant: 'warning',
          });
        }
      } else {
        setHelperAcademicYear(null);
        setAcademicYearMismatch(false);
        setFeeInstallments([]);
        setSelectedStudent(null);
        setFeeSummary(null);
        setAlert({
          show: true,
          message:
            response.data.message || 'No installments found for this student',
          variant: 'warning',
        });
      }
    } catch (error) {
      setHelperAcademicYear(null);
      setAcademicYearMismatch(false);
      setFeeInstallments([]);
      setSelectedStudent(null);
      setFeeSummary(null);
      setAlert({
        show: true,
        message: 'Error loading student installments',
        variant: 'danger',
      });
    } finally {
      setLoadingInstallments(false);
    }
  };

  const fetchReceipt = async () => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      return;
    }
    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-one-receipt-v2?id=${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      // Merge fee_installments into receipt
      const receiptData = response.data.receipt || {};
      const apiAcademicYear = response.data.academic_year || null;
      const conflict = response.data.academic_year_conflict || false;

      const installments = (response.data.fee_installments || []).map(
        (inst) => ({
          ...inst,
          allocated_amount:
            inst.allocated_amount ?? inst.installment_amount ?? '',
          editable_balance_amount:
            Number(inst.balance_amount ?? 0) +
            Number(inst.allocated_amount ?? inst.installment_amount ?? 0),
          is_selected:
            Number(inst.allocated_amount ?? inst.installment_amount ?? 0) > 0,
          is_editable_existing: true,
        }),
      );
      const hasExistingInstallments = installments.length > 0;

      setReceipt({
        ...receiptData,
        admission_no: hasExistingInstallments
          ? receiptData.admission_no || ''
          : '',
        academic_year_id:
          selectedAcademicYear?.id || receiptData.academic_year_id || '',
        fee_installments: installments,
      });
      setFeeInstallments(installments);
      setHelperAcademicYear(apiAcademicYear);
      setAcademicYearMismatch(
        !!apiAcademicYear &&
          !!selectedAcademicYear?.id &&
          apiAcademicYear.id !== selectedAcademicYear.id,
      );
      setSelectedStudent(
        hasExistingInstallments && receiptData.student_id
          ? {
              id: receiptData.student_id,
              name: receiptData.student_name,
              admission_no: receiptData.admission_no,
            }
          : null,
      );
      setFeeSummary(null);

      if (hasExistingInstallments && receiptData.admission_no) {
        const academicYearId =
          selectedAcademicYear?.id || receiptData.academic_year_id || '';

        if (academicYearId) {
          fetchStudentFeeInstallments(
            receiptData.admission_no,
            academicYearId,
            installments,
          );
        }
      }
    } catch (error) {
      console.error('Error fetching receipt:', error);
      setAlert({
        show: true,
        message: 'Failed to load receipt',
        variant: 'danger',
      });
    }
  };

  const fetchAccounts = async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-accounts?all=1`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setAccounts(response.data.data || []);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  const fetchCashAccounts = async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get-accounts`,
        { account_type_ids: [9, 10] },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      setCashAccounts(response.data.data || []);
    } catch (error) {
      console.error('Error fetching cash accounts:', error);
    }
  };

  const handleFromAccountChange = (e) => {
    const input = e.target.value;
    setReceipt({ ...receipt, from_account: input, from_account_id: '' });
    setShowFromDropdown(true);
    setFromDropdownIndex(-1);
    if (input.trim() === '') {
      setFilteredFromAccounts([]);
    } else {
      const matches = accounts.filter((acc) =>
        acc.name.toLowerCase().includes(input.toLowerCase()),
      );
      setFilteredFromAccounts(matches);
    }
  };

  const selectFromAccount = (account) => {
    setReceipt({
      ...receipt,
      from_account_id: account.id,
      from_account: account.name,
    });
    setFilteredFromAccounts([]);
    setShowFromDropdown(false);
  };

  const handleToAccountChange = (e) => {
    const input = e.target.value;
    setReceipt({ ...receipt, to_account: input, to_account_id: '' });
    setShowToDropdown(true);
    setToDropdownIndex(-1);
    if (input.trim() === '') {
      setFilteredToAccounts([]);
    } else {
      const matches = cashAccounts.filter((acc) =>
        acc.name.toLowerCase().includes(input.toLowerCase()),
      );
      setFilteredToAccounts(matches);
    }
  };

  const selectToAccount = (account) => {
    setReceipt({
      ...receipt,
      to_account_id: account.id,
      to_account: account.name,
    });
    setFilteredToAccounts([]);
    setShowToDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return; // Prevent double submit

    // Validation
    if (!receipt.from_account_id || !receipt.to_account_id) {
      setAlert({
        show: true,
        message: 'Please select both accounts',
        variant: 'warning',
      });
      return;
    }
    if (academicYearMismatch) {
      setAlert({
        show: true,
        message:
          'Cannot save because selected academic year does not match student installments.',
        variant: 'danger',
      });
      return;
    }
    if (!receipt.amount || parseFloat(receipt.amount) <= 0) {
      setAlert({
        show: true,
        message: 'Please enter a valid amount',
        variant: 'warning',
      });
      return;
    }

    setLoading(true);
    setAlert({ show: false, message: '', variant: '' });
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      setLoading(false);
      return;
    }

    // Prepare payload for v2
    let payload = {
      receipt_id: id,
      from_account_id: receipt.from_account_id,
      to_account_id: receipt.to_account_id,
      amount: parseFloat(receipt.amount),
      date: receipt.date,
      description: receipt.description || '',
      admission_no: receipt.admission_no,
      academic_year_id: receipt.academic_year_id,
    };
    // If fee_installments exist, add to payload (only those with amount > 0)
    if (Array.isArray(feeInstallments) && feeInstallments.length > 0) {
      payload.fee_installments = feeInstallments
        .filter((inst) => inst.is_selected && Number(inst.allocated_amount) > 0)
        .map((inst) => ({
          student_fee_installment_id: inst.student_fee_installment_id,
          amount: Number(inst.allocated_amount),
        }));
    }

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}update-receipt-v2`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      setAlert({
        show: true,
        message: response.data.message || 'Receipt updated successfully!',
        variant: 'success',
      });
      // Keep loading true until redirect to prevent further saves
      setTimeout(() => {
        setLoading(false);
        navigate('/receipt-list');
      }, 1500);
    } catch (error) {
      console.error('Error updating receipt:', error);
      setAlert({
        show: true,
        message:
          error.response?.data?.message ||
          'Error updating receipt. Please try again.',
        variant: 'danger',
      });
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div
        style={{
          background: 'white',
          paddingTop: '15px',
        }}
      >
        <div
          style={{
            maxWidth: '800px',
            margin: '0 auto',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            padding: 0,
          }}
        >
          <div
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '12px 12px 0 0',
              padding: '16px 24px',
              marginBottom: 0,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <FaReceipt style={{ color: 'white', fontSize: '1.5rem' }} />
              <h3
                style={{
                  color: 'white',
                  margin: 0,
                  fontSize: '1.2rem',
                  fontWeight: '700',
                }}
              >
                Edit Receipt
              </h3>
            </div>
            <Button
              variant="link"
              onClick={() => navigate(-1)}
              style={{
                color: 'white',
                background: 'transparent',
                border: 'none',
                fontSize: '1.5rem',
                padding: 0,
                marginLeft: 'auto',
                minWidth: 0,
                minHeight: 0,
                boxShadow: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
              aria-label="Close"
              title="Close (Esc)"
            >
              <FaTimes />
            </Button>
          </div>
          {alert.show && (
            <div
              style={{
                position: 'fixed',
                left: '18px',
                bottom: '18px',
                zIndex: 9999,
                minWidth: '180px',
                maxWidth: '320px',
                pointerEvents: 'auto',
                boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
              }}
            >
              <div
                style={{
                  position: 'relative',
                  borderRadius: '6px',
                  background:
                    alert.variant === 'success'
                      ? '#e6f7e6'
                      : alert.variant === 'danger'
                        ? '#ffeaea'
                        : '#fff8e1',
                  color: '#5c4200',
                  marginBottom: 0,
                  fontWeight: 500,
                  fontSize: '0.92rem',
                  padding: '6px 32px 6px 12px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                  minHeight: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  maxWidth: '320px',
                }}
              >
                <span style={{ flex: 1 }}>{alert.message}</span>
                <button
                  onClick={() =>
                    setAlert({ show: false, message: '', variant: '' })
                  }
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: '8px',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#5c4200',
                    fontSize: '1.1rem',
                    lineHeight: 1,
                    padding: '0 2px',
                    cursor: 'pointer',
                    borderRadius: '2px',
                    height: '22px',
                    width: '22px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>
            </div>
          )}
          <div style={{ padding: '30px' }}>
            <Form onSubmit={handleSubmit}>
              {/* Receipt Number, Admission No, Date Row */}
              <div
                style={{ display: 'flex', gap: '20px', marginBottom: '24px' }}
              >
                <Form.Group style={{ flex: '1' }}>
                  <Form.Label
                    style={{
                      fontWeight: '600',
                      color: '#2c3e50',
                      fontSize: '0.9rem',
                    }}
                  >
                    Receipt No.
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={receipt.receipt_number || ''}
                    disabled
                    style={{
                      borderRadius: '6px',
                      padding: '10px 12px',
                      border: '1px solid #dee2e6',
                      fontSize: '0.9rem',
                      backgroundColor: '#f8f9fa',
                      color: '#6c757d',
                      fontWeight: '600',
                    }}
                  />
                </Form.Group>
                <Form.Group style={{ flex: '1' }}>
                  <Form.Label
                    style={{
                      fontWeight: '600',
                      color: '#2c3e50',
                      fontSize: '0.9rem',
                    }}
                  >
                    Admission No.
                  </Form.Label>
                  <Form.Control
                    type="number"
                    value={receipt.admission_no || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (!value || value.toString().trim() === '') {
                        setHelperAcademicYear(null);
                        setAcademicYearMismatch(false);
                        setSelectedStudent(null);
                        setFeeSummary(null);
                        setFeeInstallments([]);
                        setReceipt({
                          ...receipt,
                          admission_no: '',
                          fee_installments: [],
                          amount: '',
                        });
                      } else {
                        setReceipt({ ...receipt, admission_no: value });
                      }
                    }}
                    onBlur={(e) => {
                      if (e.target.value && receipt.academic_year_id) {
                        fetchStudentFeeInstallments(
                          e.target.value,
                          receipt.academic_year_id,
                          feeInstallments,
                        );
                      }
                    }}
                    onKeyDown={(e) => {
                      if (
                        (e.key === 'Enter' || e.key === 'Tab') &&
                        receipt.admission_no &&
                        receipt.academic_year_id
                      ) {
                        fetchStudentFeeInstallments(
                          receipt.admission_no,
                          receipt.academic_year_id,
                          feeInstallments,
                        );
                      }
                    }}
                    placeholder="Enter admission number"
                    style={{
                      borderRadius: '6px',
                      padding: '10px 12px',
                      border: '1px solid #dee2e6',
                      fontSize: '0.9rem',
                    }}
                  />
                  {academicYearMismatch && helperAcademicYear && (
                    <div
                      style={{
                        marginTop: '6px',
                        color: '#c52828',
                        fontSize: '0.82rem',
                        fontWeight: 500,
                      }}
                    >
                      Installments year: {helperAcademicYear.name} (selected{' '}
                      {selectedAcademicYear?.name || 'N/A'}) - mismatch
                    </div>
                  )}
                </Form.Group>
                <Form.Group style={{ flex: '1' }}>
                  <Form.Label
                    style={{
                      fontWeight: '600',
                      color: '#2c3e50',
                      fontSize: '0.9rem',
                    }}
                  >
                    Date <span style={{ color: '#dc3545' }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    ref={dateInputRef}
                    value={receipt.date}
                    onChange={(e) =>
                      setReceipt({ ...receipt, date: e.target.value })
                    }
                    style={{
                      borderRadius: '6px',
                      padding: '10px 12px',
                      border: '1px solid #dee2e6',
                      fontSize: '0.9rem',
                    }}
                    required
                  />
                </Form.Group>
              </div>
              {/* Fee Installments Editing UI */}
              {feeInstallments.length > 0 && (
                <div
                  style={{
                    marginBottom: '24px',
                    background: '#f8f9ff',
                    borderRadius: '8px',
                    padding: '16px',
                    border: '1px solid #e3e7ff',
                  }}
                >
                  <h6
                    style={{
                      fontWeight: '600',
                      color: '#2c3e50',
                      fontSize: '1rem',
                      marginBottom: '8px',
                    }}
                  >
                    Installments
                  </h6>
                  {academicYearMismatch && (
                    <div
                      style={{
                        marginBottom: '8px',
                        padding: '10px',
                        borderRadius: '6px',
                        background: '#fff3cd',
                        border: '1px solid #ffeeba',
                        color: '#856404',
                        fontSize: '0.9rem',
                      }}
                    >
                      Academic year mismatch: installments belong to{' '}
                      {helperAcademicYear?.name || 'another year'} but this
                      receipt is for {selectedAcademicYear?.name || 'current'}.
                      Amount fields are read-only and you cannot save until year
                      matches.
                    </div>
                  )}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    {feeInstallments.map((inst, idx) => (
                      <div
                        key={inst.student_fee_installment_id}
                        style={{
                          display: 'grid',
                          alignItems: 'center',
                          gridTemplateColumns:
                            '24px minmax(180px, 2fr) minmax(110px, 1fr) minmax(90px, 1fr) 100px 120px',
                          gap: '10px',
                          background: inst.is_selectable_for_receipt
                            ? '#f0fff4'
                            : '#f8f9fa',
                          borderRadius: '6px',
                          border: '1px solid #dee2e6',
                          padding: '8px 12px',
                        }}
                      >
                        <input
                          type="checkbox"
                          disabled={
                            !canInteractWithInstallment(
                              inst,
                              academicYearMismatch,
                            )
                          }
                          checked={!!inst.is_selected}
                          onChange={(e) => {
                            if (
                              !canInteractWithInstallment(
                                inst,
                                academicYearMismatch,
                              )
                            )
                              return;

                            const checked = e.target.checked;
                            setFeeInstallments((prev) => {
                              const updatedInstallments = prev.map((item, i) =>
                                i === idx
                                  ? {
                                      ...item,
                                      is_selected: checked,
                                      allocated_amount: checked
                                        ? item.allocated_amount ||
                                          getInstallmentEditableBalance(item)
                                        : '',
                                    }
                                  : item,
                              );

                              setReceipt((current) => ({
                                ...current,
                                amount:
                                  calculateInstallmentTotal(
                                    updatedInstallments,
                                  ).toString(),
                              }));

                              return updatedInstallments;
                            });
                          }}
                        />
                        <div
                          style={{
                            fontWeight: '600',
                            color: '#2c3e50',
                            fontSize: '0.9rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {inst.title}
                        </div>
                        <div style={{ color: '#495057', fontSize: '0.85rem' }}>
                          ₹{getInstallmentEditableBalance(inst)}
                        </div>
                        <div
                          style={{
                            color: '#667eea',
                            fontSize: '0.9rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {inst.fee_structure_name}
                        </div>
                        <input
                          type="number"
                          min="0"
                          max={getInstallmentEditableBalance(inst)}
                          value={inst.allocated_amount || ''}
                          disabled={
                            !canInteractWithInstallment(
                              inst,
                              academicYearMismatch,
                            ) || !inst.is_selected
                          }
                          onChange={(e) => {
                            if (
                              !canInteractWithInstallment(
                                inst,
                                academicYearMismatch,
                              ) ||
                              !inst.is_selected
                            )
                              return;

                            const val = e.target.value;
                            setFeeInstallments((prev) => {
                              const updatedInstallments = prev.map((item, i) =>
                                i === idx
                                  ? { ...item, allocated_amount: val }
                                  : item,
                              );

                              setReceipt((current) => ({
                                ...current,
                                amount:
                                  calculateInstallmentTotal(
                                    updatedInstallments,
                                  ).toString(),
                              }));

                              return updatedInstallments;
                            });
                          }}
                          style={{
                            width: '100%',
                            borderRadius: '4px',
                            border: '1px solid #dee2e6',
                            padding: '4px 8px',
                            fontSize: '0.9rem',
                          }}
                          placeholder="Amount"
                        />
                        <div
                          style={{
                            color: '#495057',
                            fontSize: '0.85rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {inst.academic_year_name || ''}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* From and To Accounts Row */}
              <div
                style={{ display: 'flex', gap: '20px', marginBottom: '24px' }}
              >
                {/* From Account Field */}
                <Form.Group style={{ flex: '1' }} ref={fromDropdownRef}>
                  <Form.Label
                    style={{
                      fontWeight: '600',
                      color: '#2c3e50',
                      fontSize: '0.9rem',
                    }}
                  >
                    Received From <span style={{ color: '#dc3545' }}>*</span>
                  </Form.Label>
                  <div style={{ position: 'relative' }}>
                    <Form.Control
                      type="text"
                      value={receipt.from_account}
                      onChange={handleFromAccountChange}
                      onFocus={() => setShowFromDropdown(true)}
                      placeholder="Search account..."
                      style={{
                        borderRadius: '6px',
                        padding: '10px 12px',
                        border: receipt.from_account_id
                          ? '1px solid #28a745'
                          : '1px solid #dee2e6',
                        fontSize: '0.9rem',
                        backgroundColor: receipt.from_account_id
                          ? '#f0fff4'
                          : 'white',
                      }}
                      required
                    />
                    {receipt.from_account_id && (
                      <FaCheck
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#28a745',
                        }}
                      />
                    )}
                    {showFromDropdown && filteredFromAccounts.length > 0 && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: 'white',
                          border: '1px solid #dee2e6',
                          borderRadius: '6px',
                          marginTop: '4px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          zIndex: 1000,
                        }}
                      >
                        {filteredFromAccounts.map((acc, idx) => (
                          <div
                            key={acc.id}
                            onClick={() => selectFromAccount(acc)}
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f0f0f0',
                              fontSize: '0.9rem',
                              transition: 'background 0.2s',
                              background:
                                idx === fromDropdownIndex ? '#e9ecef' : 'white',
                            }}
                            onMouseEnter={() => setFromDropdownIndex(idx)}
                            onMouseLeave={() => setFromDropdownIndex(-1)}
                          >
                            {acc.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Form.Group>
                {/* To Account Field */}
                <Form.Group style={{ flex: '1' }} ref={toDropdownRef}>
                  <Form.Label
                    style={{
                      fontWeight: '600',
                      color: '#2c3e50',
                      fontSize: '0.9rem',
                    }}
                  >
                    To Account (Cash/Bank){' '}
                    <span style={{ color: '#dc3545' }}>*</span>
                  </Form.Label>
                  <div style={{ position: 'relative' }}>
                    <Form.Control
                      type="text"
                      value={receipt.to_account}
                      onChange={handleToAccountChange}
                      onFocus={() => setShowToDropdown(true)}
                      placeholder="Search cash/bank..."
                      style={{
                        borderRadius: '6px',
                        padding: '10px 12px',
                        border: receipt.to_account_id
                          ? '1px solid #28a745'
                          : '1px solid #dee2e6',
                        fontSize: '0.9rem',
                        backgroundColor: receipt.to_account_id
                          ? '#f0fff4'
                          : 'white',
                      }}
                      required
                    />
                    {receipt.to_account_id && (
                      <FaCheck
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#28a745',
                        }}
                      />
                    )}
                    {showToDropdown && filteredToAccounts.length > 0 && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: 'white',
                          border: '1px solid #dee2e6',
                          borderRadius: '6px',
                          marginTop: '4px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          zIndex: 1000,
                        }}
                      >
                        {filteredToAccounts.map((acc, idx) => (
                          <div
                            key={acc.id}
                            onClick={() => selectToAccount(acc)}
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f0f0f0',
                              fontSize: '0.9rem',
                              transition: 'background 0.2s',
                              background:
                                idx === toDropdownIndex ? '#e9ecef' : 'white',
                            }}
                            onMouseEnter={() => setToDropdownIndex(idx)}
                            onMouseLeave={() => setToDropdownIndex(-1)}
                          >
                            {acc.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Form.Group>
              </div>
              {/* Amount & Description Two-Column Row */}
              <div
                style={{ display: 'flex', gap: '20px', marginBottom: '24px' }}
              >
                {/* Amount Field - Prominent */}
                <Form.Group style={{ flex: '1' }}>
                  <Form.Label
                    style={{
                      fontWeight: '600',
                      color: '#2c3e50',
                      fontSize: '1rem',
                    }}
                  >
                    Amount <span style={{ color: '#dc3545' }}>*</span>
                  </Form.Label>
                  <div style={{ position: 'relative' }}>
                    <span
                      style={{
                        position: 'absolute',
                        left: '16px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#667eea',
                        fontWeight: '700',
                        fontSize: '1.2rem',
                      }}
                    >
                      ₹
                    </span>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={receipt.amount}
                      onChange={(e) =>
                        setReceipt({ ...receipt, amount: e.target.value })
                      }
                      placeholder="0.00"
                      style={{
                        borderRadius: '8px',
                        padding: '14px 16px 14px 40px',
                        border: '2px solid #667eea',
                        fontSize: '1.1rem',
                        fontWeight: '600',
                        backgroundColor: '#f8f9ff',
                        color: '#2c3e50',
                      }}
                      required
                    />
                  </div>
                </Form.Group>
                {/* Description Field (expandable/collapsible) */}
                <Form.Group style={{ flex: '1' }}>
                  <Form.Label
                    style={{
                      fontWeight: '600',
                      color: '#2c3e50',
                      fontSize: '0.9rem',
                    }}
                  >
                    Description
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={1}
                    value={receipt.description}
                    onChange={(e) =>
                      setReceipt({ ...receipt, description: e.target.value })
                    }
                    onFocus={(e) => (e.target.style.height = '72px')}
                    onBlur={(e) => {
                      if (!e.target.value) e.target.style.height = '55px';
                    }}
                    onInput={(e) => {
                      if (e.target.value) e.target.style.height = '72px';
                      else e.target.style.height = '55px';
                    }}
                    placeholder="Add any notes or description..."
                    style={{
                      borderRadius: '6px',
                      padding: '10px 12px',
                      border: '1px solid #dee2e6',
                      fontSize: '0.9rem',
                      resize: 'none',
                      height: receipt.description ? '72px' : '55px',
                      transition: 'height 0.2s',
                    }}
                  />
                </Form.Group>
              </div>
              {/* Action Buttons */}
              <div
                style={{
                  display: 'flex',
                  gap: '10px',
                  justifyContent: 'flex-end',
                }}
              >
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="save-tooltip">Save (Ctrl+S)</Tooltip>}
                >
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={loading || academicYearMismatch}
                    style={{
                      background:
                        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '10px 28px',
                      fontWeight: '600',
                      fontSize: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      minWidth: '100px',
                      position: 'relative',
                    }}
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <FaSave size={20} />
                        <span>Save</span>
                      </>
                    )}
                  </Button>
                </OverlayTrigger>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </Layout>
  );
};
export default EditReceipt;
